import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LoginView } from "@/components/auth/LoginView";
import { AppShell } from "@/components/AppShell";
import { ProjectsDashboard } from "@/components/dashboard/ProjectsDashboard";
import { ProjectDetailView } from "@/components/projects/ProjectDetailView";
import { SettingsView } from "@/components/settings/SettingsView";
import { ManuscriptLanding } from "@/components/manuscript/ManuscriptLanding";
import { ManuscriptView } from "@/components/manuscript/ManuscriptView";
import { ChapterManager } from "@/components/manuscript/ChapterManager";
import { CharacterStudio } from "@/components/characters/CharacterStudio";
import { CharacterLanding } from "@/components/characters/CharacterLanding";
import { PronunciationLanding } from "@/components/pronunciations/PronunciationLanding";
import { PronunciationManager } from "@/components/pronunciations/PronunciationManager";
import { ProductionLanding } from "@/components/production/ProductionLanding";
import { ProductionDesk } from "@/components/production/ProductionDesk";
import { AssemblyLanding } from "@/components/assembly/AssemblyLanding";
import { AssemblyWorkspace } from "@/components/assembly/AssemblyWorkspace";
import BetaPreview from "@/components/beta/BetaPreview";
import ListenApp from "@/components/listen/ListenApp";
import { StudioLanding } from "@/components/studio/StudioLanding";
import { StudioSync } from "@/components/studio/StudioSync";
import LandingPage from "@/components/landing/LandingPage";
import PrivacyPage from "@/components/landing/PrivacyPage";
import { Loader2 } from "lucide-react";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function LoginGate() {
  const { user } = useAuth();
  if (user) return <Navigate to="/studio/projects" replace />;
  return <LoginView />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/login" element={<LoginGate />} />
            <Route path="/listen/:token" element={<ListenApp />} />
            <Route path="/listen" element={<ListenApp />} />
            <Route
              path="/studio"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route index element={<Navigate to="projects" replace />} />
              <Route path="projects" element={<ProjectsDashboard />} />
              <Route path="projects/:projectId" element={<ProjectDetailView />} />
              <Route path="projects/:projectId/manuscript" element={<ManuscriptView />} />
              <Route path="projects/:projectId/chapters" element={<ChapterManager />} />
              <Route path="projects/:projectId/characters" element={<CharacterStudio />} />
              <Route path="projects/:projectId/pronunciations" element={<PronunciationManager />} />
              <Route path="projects/:projectId/production" element={<ProductionDesk />} />
              <Route path="projects/:projectId/assembly" element={<AssemblyWorkspace />} />
              <Route path="projects/:projectId/beta" element={<BetaPreview />} />
              <Route path="projects/:projectId/studio" element={<StudioSync />} />
              <Route path="settings" element={<SettingsView />} />
              <Route path="manuscript" element={<ManuscriptLanding />} />
              <Route path="characters" element={<CharacterLanding />} />
              <Route path="pronunciations" element={<PronunciationLanding />} />
              <Route path="production" element={<ProductionLanding />} />
              <Route path="review" element={<ProductionLanding />} />
              <Route path="assembly" element={<AssemblyLanding />} />
              <Route path="exports" element={<AssemblyLanding />} />
              <Route path="studio-sync" element={<StudioLanding />} />
              <Route path="beta" element={<BetaPreview />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
